import React, { useState, useEffect } from 'react';
import { useUserStore } from '../stores/useUserStore';
import { useTranslation } from '../hooks/useTranslation';
import { useNotification } from '../hooks/useNotification';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Gift,
  Star,
  DollarSign,
  ShoppingCart,
  Clock,
  Trophy,
  Sparkles,
  Coins,
  CreditCard,
  Package,
  Heart,
  Zap,
} from 'lucide-react';
import { format } from 'date-fns';

interface StoreItem {
  id: string;
  name: string;
  description: string;
  category: 'cash' | 'items' | 'gacha' | 'experiences';
  price: number; // in points
  cashValue?: number; // in dollars
  image?: string;
  stock?: number;
  isLimited: boolean;
  isPopular: boolean;
  tags: string[];
}

interface UserPoints {
  total: number;
  available: number;
  pending: number;
  lifetime: number;
}

interface RedemptionHistory {
  id: string;
  item: StoreItem;
  pointsUsed: number;
  redeemedAt: string;
  status: 'pending' | 'approved' | 'completed' | 'cancelled';
}

const ModernPointStore: React.FC = () => {
  const { translate } = useTranslation();
  const { notify } = useNotification();
  const { user } = useUserStore();
  const [userPoints, setUserPoints] = useState<UserPoints>({
    total: 0,
    available: 0,
    pending: 0,
    lifetime: 0,
  });
  const [storeItems, setStoreItems] = useState<StoreItem[]>([]);
  const [redemptionHistory, setRedemptionHistory] = useState<
    RedemptionHistory[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Mock data for demonstration
  const mockStoreItems: StoreItem[] = [
    {
      id: '1',
      name: 'Cash Redemption - $10',
      description: 'Redeem 100 points for $10 cash',
      category: 'cash',
      price: 100,
      cashValue: 10,
      isLimited: false,
      isPopular: true,
      tags: ['cash', 'money'],
    },
    {
      id: '2',
      name: 'Cash Redemption - $25',
      description: 'Redeem 250 points for $25 cash',
      category: 'cash',
      price: 250,
      cashValue: 25,
      isLimited: false,
      isPopular: true,
      tags: ['cash', 'money'],
    },
    {
      id: '3',
      name: 'Coffee Shop Gift Card',
      description: '$20 gift card for any coffee shop',
      category: 'items',
      price: 200,
      stock: 50,
      isLimited: true,
      isPopular: false,
      tags: ['gift card', 'coffee'],
    },
    {
      id: '4',
      name: 'Premium Coffee Beans',
      description: '1lb bag of premium single-origin coffee beans',
      category: 'items',
      price: 150,
      stock: 20,
      isLimited: true,
      isPopular: false,
      tags: ['coffee', 'premium'],
    },
    {
      id: '5',
      name: 'Mystery Box',
      description: 'Random surprise items worth 50-200 points',
      category: 'gacha',
      price: 100,
      isLimited: false,
      isPopular: true,
      tags: ['surprise', 'mystery'],
    },
    {
      id: '6',
      name: 'Golden Ticket',
      description: 'Special access to exclusive events and items',
      category: 'gacha',
      price: 500,
      isLimited: true,
      isPopular: false,
      tags: ['exclusive', 'special'],
    },
    {
      id: '7',
      name: 'Coffee Tasting Experience',
      description: 'Private coffee cupping session with expert',
      category: 'experiences',
      price: 300,
      isLimited: true,
      isPopular: false,
      tags: ['experience', 'learning'],
    },
    {
      id: '8',
      name: 'Store Manager for a Day',
      description: 'Shadow the store manager for a full day',
      category: 'experiences',
      price: 400,
      isLimited: true,
      isPopular: false,
      tags: ['experience', 'leadership'],
    },
  ];

  const mockUserPoints: UserPoints = {
    total: 1250,
    available: 1000,
    pending: 250,
    lifetime: 5000,
  };

  const mockRedemptionHistory: RedemptionHistory[] = [
    {
      id: '1',
      item: mockStoreItems[0],
      pointsUsed: 100,
      redeemedAt: '2024-01-10T10:30:00Z',
      status: 'completed',
    },
    {
      id: '2',
      item: mockStoreItems[4],
      pointsUsed: 100,
      redeemedAt: '2024-01-12T14:20:00Z',
      status: 'approved',
    },
  ];

  useEffect(() => {
    loadStoreData();
  }, []);

  const loadStoreData = async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      setUserPoints(mockUserPoints);
      setStoreItems(mockStoreItems);
      setRedemptionHistory(mockRedemptionHistory);
    } catch (error) {
      console.error('Error loading store data:', error);
      notify('Failed to load store data', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleRedeemItem = (item: StoreItem) => {
    if (userPoints.available < item.price) {
      notify('Insufficient points for this item', { type: 'error' });
      return;
    }

    if (item.stock !== undefined && item.stock <= 0) {
      notify('This item is out of stock', { type: 'error' });
      return;
    }

    // Simulate redemption
    const newRedemption: RedemptionHistory = {
      id: Date.now().toString(),
      item,
      pointsUsed: item.price,
      redeemedAt: new Date().toISOString(),
      status: 'pending',
    };

    setRedemptionHistory((prev) => [newRedemption, ...prev]);
    setUserPoints((prev) => ({
      ...prev,
      available: prev.available - item.price,
      pending: prev.pending + item.price,
    }));

    if (item.stock !== undefined) {
      setStoreItems((prev) =>
        prev.map((storeItem) =>
          storeItem.id === item.id
            ? { ...storeItem, stock: (storeItem.stock || 0) - 1 }
            : storeItem
        )
      );
    }

    notify(`Successfully redeemed ${item.name}!`, { type: 'success' });
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'cash':
        return <DollarSign className="h-5 w-5" />;
      case 'items':
        return <Package className="h-5 w-5" />;
      case 'gacha':
        return <Sparkles className="h-5 w-5" />;
      case 'experiences':
        return <Heart className="h-5 w-5" />;
      default:
        return <Gift className="h-5 w-5" />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'cash':
        return 'bg-green-100 text-green-800';
      case 'items':
        return 'bg-blue-100 text-blue-800';
      case 'gacha':
        return 'bg-purple-100 text-purple-800';
      case 'experiences':
        return 'bg-pink-100 text-pink-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredItems = storeItems.filter((item) => {
    const matchesCategory =
      selectedCategory === 'all' || item.category === selectedCategory;
    const matchesSearch =
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.tags.some((tag) =>
        tag.toLowerCase().includes(searchQuery.toLowerCase())
      );
    return matchesCategory && matchesSearch;
  });

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground mt-2">Loading point store...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-foreground">Point Store</h1>
        <p className="text-muted-foreground mt-2">
          Redeem your earned points for rewards
        </p>
      </div>

      {/* User Points Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Coins className="h-5 w-5 text-yellow-600" />
              <div>
                <p className="text-sm text-muted-foreground">Available</p>
                <p className="text-2xl font-bold">{userPoints.available}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-orange-600" />
              <div>
                <p className="text-sm text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold">{userPoints.pending}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Star className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="text-2xl font-bold">{userPoints.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-purple-600" />
              <div>
                <p className="text-sm text-muted-foreground">Lifetime</p>
                <p className="text-2xl font-bold">{userPoints.lifetime}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <Label htmlFor="search">Search Items</Label>
              <Input
                id="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, description, or tags..."
              />
            </div>
            <div className="md:w-48">
              <Label htmlFor="category">Category</Label>
              <select
                id="category"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full p-2 border rounded-md"
                title="Select category"
              >
                <option value="all">All Categories</option>
                <option value="cash">Cash Redemption</option>
                <option value="items">Items</option>
                <option value="gacha">Gacha</option>
                <option value="experiences">Experiences</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Store Items */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredItems.map((item) => (
          <Card key={item.id} className="relative">
            {item.isPopular && (
              <div className="absolute top-2 right-2 z-10">
                <Badge className="bg-red-500 text-white">
                  <Zap className="h-3 w-3 mr-1" />
                  Popular
                </Badge>
              </div>
            )}
            {item.isLimited && (
              <div className="absolute top-2 left-2 z-10">
                <Badge variant="secondary">Limited</Badge>
              </div>
            )}

            <CardHeader>
              <div className="flex items-center gap-2 mb-2">
                {getCategoryIcon(item.category)}
                <Badge className={getCategoryColor(item.category)}>
                  {item.category}
                </Badge>
              </div>
              <CardTitle className="text-lg">{item.name}</CardTitle>
              <CardDescription>{item.description}</CardDescription>
            </CardHeader>

            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <Coins className="h-4 w-4 text-yellow-600" />
                    <span className="font-semibold">{item.price} points</span>
                  </div>
                  {item.cashValue && (
                    <div className="flex items-center gap-1 text-green-600">
                      <DollarSign className="h-4 w-4" />
                      <span className="text-sm">${item.cashValue}</span>
                    </div>
                  )}
                </div>

                {item.stock !== undefined && (
                  <div className="text-sm text-muted-foreground">
                    Stock: {item.stock} remaining
                  </div>
                )}

                <div className="flex flex-wrap gap-1">
                  {item.tags.map((tag, index) => (
                    <Badge key={index} variant="outline" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>

                <Button
                  className="w-full"
                  onClick={() => handleRedeemItem(item)}
                  disabled={
                    userPoints.available < item.price ||
                    (item.stock !== undefined && item.stock <= 0)
                  }
                >
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  {userPoints.available < item.price
                    ? 'Insufficient Points'
                    : item.stock !== undefined && item.stock <= 0
                    ? 'Out of Stock'
                    : 'Redeem Now'}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Redemption History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Recent Redemptions
          </CardTitle>
          <CardDescription>
            Your recent point redemptions and their status
          </CardDescription>
        </CardHeader>
        <CardContent>
          {redemptionHistory.length === 0 ? (
            <div className="text-center py-8">
              <Gift className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No redemptions yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {redemptionHistory.map((redemption) => (
                <div
                  key={redemption.id}
                  className="flex items-center justify-between p-4 border rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    {getCategoryIcon(redemption.item.category)}
                    <div>
                      <p className="font-medium">{redemption.item.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {redemption.pointsUsed} points •{' '}
                        {format(
                          new Date(redemption.redeemedAt),
                          'MMM dd, yyyy'
                        )}
                      </p>
                    </div>
                  </div>
                  <Badge
                    className={
                      redemption.status === 'completed'
                        ? 'bg-green-100 text-green-800'
                        : redemption.status === 'approved'
                        ? 'bg-blue-100 text-blue-800'
                        : redemption.status === 'pending'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }
                  >
                    {redemption.status}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ModernPointStore;
